import {
  create as createAxios,
  isAxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { emitUnauthorized } from './auth/authEvents';
import { authClient } from './authClient';
import { axiosConfig, camelizeResponse } from './axiosConfig';
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  getStoredTokenExpires,
  storeAuthTokens,
  type StoredAuthTokens,
} from './tokenStorage';

export type RefreshResponse = StoredAuthTokens;

// Module-level token slots. Kept with the authenticated client because both
// the request interceptor and the auto-refresh path need to read them.
let accessToken: string | null = null;
let refreshToken: string | null = null;
let tokenExpires: string | null = null;
let refreshPromise: Promise<RefreshResponse | null> | null = null;
let hydratePromise: Promise<void> | null = null;
let hasHydratedTokens = false;

export const apiClient: AxiosInstance = createAxios(axiosConfig);

apiClient.interceptors.response.use(camelizeResponse);

export async function setAuthTokens(tokens: StoredAuthTokens): Promise<void> {
  accessToken = tokens.token;
  refreshToken = tokens.refresh ?? refreshToken;
  tokenExpires = tokens.expires;
  hasHydratedTokens = true;
  await storeAuthTokens(tokens);
}

export async function clearAuthTokens(): Promise<void> {
  accessToken = null;
  refreshToken = null;
  tokenExpires = null;
  hasHydratedTokens = true;
  await clearStoredAuthTokens();
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

export function isUnauthorizedError(error: unknown): boolean {
  return getApiErrorStatus(error) === 401;
}

async function rejectUnauthorized(error: unknown): Promise<never> {
  await clearAuthTokens();
  emitUnauthorized();
  return Promise.reject(error);
}

function isTokenFresh(expires: string | null): boolean {
  return expires ? Date.now() < new Date(expires).getTime() - 30_000 : false;
}

async function hydrateTokensFromStorage(): Promise<void> {
  if (hasHydratedTokens) return;
  hydratePromise ??= (async () => {
    const [storedToken, storedRefreshToken, storedExpires] = await Promise.all([
      getStoredAccessToken(),
      getStoredRefreshToken(),
      getStoredTokenExpires(),
    ]);
    accessToken = storedToken;
    refreshToken = storedRefreshToken;
    tokenExpires = storedExpires;
    hasHydratedTokens = true;
  })().finally(() => {
    hydratePromise = null;
  });
  return hydratePromise;
}

export async function getValidAccessToken(): Promise<string | null> {
  await hydrateTokensFromStorage();
  if (accessToken && isTokenFresh(tokenExpires)) return accessToken;
  if (!refreshToken) {
    if (accessToken) await clearAuthTokens();
    return null;
  }
  const refreshed = await refreshAccessTokenNow();
  if (!refreshed) return null;
  return refreshed.token;
}

apiClient.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Matches the login app: signals Apigee to skip the Django legacy session
  // mint when this client doesn't care about the sessionid cookie path.
  // The variable is unset in our env, so this is a no-op today; included for
  // parity so an env flip works without code changes.
  if (process.env.EXPO_PUBLIC_SKIP_LEGACY_SESSION === 'true') {
    config.headers.set('X-Legacy', 'false');
  }
  return config;
});

async function performRefresh(): Promise<RefreshResponse | null> {
  if (!refreshToken) return null;
  try {
    const res = await authClient.post<RefreshResponse>('/v1/auth/refresh', {
      refresh: refreshToken,
    });
    await setAuthTokens(res.data);
    return res.data;
  } catch {
    // Refresh failed - the session is dead. Clear both tokens so the next
    // request doesn't keep retrying with a known-bad refresh.
    await clearAuthTokens();
    return null;
  }
}

/**
 * Refresh the access token using the stored refresh token. Concurrent callers
 * share the same in-flight Promise so we don't fire the endpoint twice.
 */
export function refreshAccessTokenNow(): Promise<RefreshResponse | null> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (response) => response,
  async (error: { config?: RetriableConfig; response?: { status?: number } }) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url ?? '';
    if (
      !original ||
      status !== 401 ||
      original._retry ||
      !refreshToken ||
      // Skip auto-refresh on the auth endpoints themselves: a 401 from
      // /login means bad credentials (refresh won't help), and a 401 from
      // /refresh means the refresh token itself is invalid.
      url.includes('/v1/auth/login') ||
      url.includes('/v1/auth/refresh')
    ) {
      if (status === 401) return rejectUnauthorized(error);
      return Promise.reject(error);
    }
    original._retry = true;
    const refreshed = await refreshAccessTokenNow();
    if (!refreshed) return rejectUnauthorized(error);
    original.headers.set('Authorization', `Bearer ${refreshed.token}`);
    return apiClient(original);
  }
);
